/**
 * docxTablePatch.ts
 * Persists structural table edits made on the rendered template canvas
 * (insert/delete row, insert/delete column, delete table, insert table) into
 * `word/document.xml`, so docxtemplater fills the table the user actually sees.
 *
 * Why XML and not a DOM overlay: the preview canvas shows the *template* table
 * (one sample row, `{{…}}` tokens). Replacing the *filled* table with that HTML
 * would wipe the repeated data rows, so structure has to live in the DOCX.
 *
 * Matching: rendered tables and `<w:tbl>` elements are both taken in document
 * order, top-level only (a table nested in a cell is left untouched). Rows and
 * cells carry their original index in `data-orig-row` / `data-orig-col`,
 * captured right after each render, so the final DOM order describes exactly
 * which originals were kept, cloned or dropped.
 */

import PizZip from "pizzip";
import { repairSplitMarkers } from "@/utils/docxMarker";
import { readColumnPlan, resetColumnPlan, gridColumnCount } from "@/utils/tableOps";

const TABLE_INDEX_ATTR = "data-orig-table";
const COL_COUNT_ATTR = "data-orig-col-count";
const ROW_INDEX_ATTR = "data-orig-row";
const COL_INDEX_ATTR = "data-orig-col";
const TABLE_COUNT_ATTR = "data-orig-table-count";
/** Set on a table created in the canvas: index of the table it was added after. */
export const NEW_TABLE_AFTER_ATTR = "data-new-table-after";

export type TableStructurePatch =
  | {
      kind: "update";
      tableIndex: number;
      /** Final row order; each entry is an original row index, or null for a new row. */
      rows: Array<number | null>;
      /** Final column order; each entry is an original column index, or null for a new column. */
      columns: Array<number | null>;
      /**
       * For each grid column that is `null` in `columns` (a brand-new column),
       * the plain text the user actually typed/placed in that column, per row
       * (row order). Without this the new column would be written as empty
       * cells and any `{{field}}` placed into it on the canvas would be lost.
       */
      newColumnTexts?: Record<number, string[]>;
    }
  | { kind: "delete"; tableIndex: number }
  | {
      kind: "insert";
      afterTableIndex: number | null;
      /** Plain text of every cell, row by row — whatever the canvas shows. */
      cells: string[][];
    };

export interface ApplyTablePatchesResult {
  xml: string;
  patchedCount: number;
  /** Patches that could not be written (merged cells, nested tables, …). */
  unsupported: TableStructurePatch[];
}

/* ------------------------------------------------------------------ */
/*  XML block scanning                                                */
/* ------------------------------------------------------------------ */

interface Block {
  start: number;
  end: number;
  xml: string;
}

/** True when `xml` at `index` opens element `tag` (and not `<w:tblPr>` for `w:tbl`). */
function isTagOpen(xml: string, index: number, tag: string): boolean {
  if (!xml.startsWith(`<${tag}`, index)) return false;
  const ch = xml.charAt(index + tag.length + 1);
  return ch === ">" || ch === " " || ch === "\t" || ch === "\n" || ch === "\r" || ch === "/";
}

/**
 * Find every top-level `<tag>…</tag>` block inside `xml`. Nested blocks of the
 * same tag are part of their parent block, never returned separately.
 */
export function findBlocks(xml: string, tag: string): Block[] {
  const out: Block[] = [];
  const closeTag = `</${tag}>`;
  let depth = 0;
  let blockStart = -1;
  let i = 0;

  while (i < xml.length) {
    if (xml.charAt(i) !== "<") {
      i++;
      continue;
    }
    if (isTagOpen(xml, i, tag)) {
      const gt = xml.indexOf(">", i);
      if (gt < 0) break;
      const selfClosing = xml.charAt(gt - 1) === "/";
      if (depth === 0) blockStart = i;
      if (selfClosing) {
        if (depth === 0) {
          out.push({ start: blockStart, end: gt + 1, xml: xml.slice(blockStart, gt + 1) });
          blockStart = -1;
        }
      } else {
        depth++;
      }
      i = gt + 1;
      continue;
    }
    if (xml.startsWith(closeTag, i)) {
      depth = Math.max(0, depth - 1);
      if (depth === 0 && blockStart >= 0) {
        const end = i + closeTag.length;
        out.push({ start: blockStart, end, xml: xml.slice(blockStart, end) });
        blockStart = -1;
      }
      i += closeTag.length;
      continue;
    }
    i++;
  }
  return out;
}

/** Replace the span covering `blocks` with `replacement`. */
function replaceSpan(xml: string, blocks: Block[], replacement: string): string {
  if (blocks.length === 0) return xml;
  const first = blocks[0];
  const last = blocks[blocks.length - 1];
  return xml.slice(0, first.start) + replacement + xml.slice(last.end);
}

/** Text of every `<w:t>` run — the `\s` guard keeps `<w:tc>` out of the match. */
function plainText(xml: string): string {
  const matches = xml.match(/<w:t(?:\s[^>]*)?>[\s\S]*?<\/w:t>/g) ?? [];
  return matches
    .map((t) => t.replace(/<w:t(?:\s[^>]*)?>/, "").replace(/<\/w:t>$/, ""))
    .join("");
}

/** Blank every `<w:t>` in a cloned cell so a new row starts empty. */
function clearText(cellXml: string): string {
  return cellXml.replace(
    /<w:t(\s[^>]*)?>[\s\S]*?<\/w:t>/gi,
    (_m, attrs = "") => `<w:t${attrs}></w:t>`
  );
}

/** Write `text` into the first `<w:t>` of a cell (leaves the rest cleared). */
function setCellText(cellXml: string, text: string): string {
  const escaped = escapeXmlText(text);
  if (!escaped) return cellXml;
  if (!/<w:t[\s>]/i.test(cellXml)) return cellXml;
  let first = true;
  return cellXml.replace(/<w:t(\s[^>]*)?>[\s\S]*?<\/w:t>/gi, (m, attrs = "") => {
    if (!first) return m;
    first = false;
    return `<w:t${attrs}>${escaped}</w:t>`;
  });
}

/** Loop controls (`{{#items}}` / `{{/items}}`) found in a chunk of XML. */
function loopTokens(xml: string): { opens: string[]; closes: string[] } {
  const text = plainText(xml);
  const opens = text.match(/\{\{#[^{}]*\}\}/g) ?? [];
  const closes = text.match(/\{\{\/[^{}]*\}\}/g) ?? [];
  return { opens, closes };
}

/** Prepend text to the first `<w:t>` of a block (creating a run if needed). */
function prependText(blockXml: string, text: string): string {
  if (!text) return blockXml;
  if (!/<w:t[\s>]/i.test(blockXml)) {
    return blockXml.replace(
      /<\/w:p>/i,
      `<w:r><w:t xml:space="preserve">${text}</w:t></w:r></w:p>`
    );
  }
  let done = false;
  return blockXml.replace(/<w:t(\s[^>]*)?>/i, (m) => {
    if (done) return m;
    done = true;
    return `${m}${text}`;
  });
}

/** Append text to the last `<w:t>` of a block. */
function appendText(blockXml: string, text: string): string {
  if (!text) return blockXml;
  const closes = [...blockXml.matchAll(/<\/w:t>/gi)];
  if (closes.length === 0) {
    return blockXml.replace(
      /<\/w:p>/i,
      `<w:r><w:t xml:space="preserve">${text}</w:t></w:r></w:p>`
    );
  }
  const last = closes[closes.length - 1];
  const at = last.index ?? 0;
  return blockXml.slice(0, at) + text + blockXml.slice(at);
}

/**
 * Re-attach loop controls that lived in dropped cells/rows, so removing a
 * column never leaves `{{#items}}` without its `{{/items}}`.
 */
function reattachLoopTokens(kept: string[], dropped: string[]): string[] {
  if (kept.length === 0 || dropped.length === 0) return kept;
  const opens: string[] = [];
  const closes: string[] = [];
  for (const chunk of dropped) {
    const t = loopTokens(chunk);
    opens.push(...t.opens);
    closes.push(...t.closes);
  }
  if (opens.length === 0 && closes.length === 0) return kept;

  const out = [...kept];
  const keptText = out.join("");
  const missingOpens = opens.filter((t) => !keptText.includes(t));
  const missingCloses = closes.filter((t) => !keptText.includes(t));
  if (missingOpens.length > 0) {
    out[0] = prependText(out[0], missingOpens.join(""));
  }
  if (missingCloses.length > 0) {
    out[out.length - 1] = appendText(out[out.length - 1], missingCloses.join(""));
  }
  return out;
}

/* ------------------------------------------------------------------ */
/*  DOM collection                                                    */
/* ------------------------------------------------------------------ */

/**
 * Rendered body tables in document order, excluding tables nested in another
 * table and those in page headers/footers — the latter live in header1.xml,
 * not document.xml, and would shift every index if counted.
 */
export function topLevelTables(container: HTMLElement): HTMLTableElement[] {
  if (!container) return [];
  // Scope by the whole page <section>, not just <article>: a brand-new table
  // inserted through the toolbar is appended to the <section> (outside any
  // <article>), so scoping to articles would silently drop it.
  const sections = container.querySelectorAll("section.docx");
  const scopes: ParentNode[] = sections.length > 0 ? Array.from(sections) : [container];
  return scopes
    .flatMap((scope) => Array.from(scope.querySelectorAll<HTMLTableElement>("table")))
    .filter((t) => !t.parentElement?.closest("table"))
    .filter((t) => !t.closest("header, footer, .docx-header, .docx-footer"));
}

function domRows(table: HTMLTableElement): HTMLTableRowElement[] {
  return Array.from(
    table.querySelectorAll<HTMLTableRowElement>(
      ":scope > tr, :scope > thead > tr, :scope > tbody > tr, :scope > tfoot > tr"
    )
  );
}

/**
 * Tag tables/rows/cells with their original position. Call this right after a
 * render: it declares "the canvas currently matches the DOCX", which is what
 * every later diff is measured against.
 */
export function snapshotTableStructure(container: HTMLElement): void {
  if (!container) return;
  const tables = topLevelTables(container);
  tables.forEach((table, ti) => {
    table.setAttribute(TABLE_INDEX_ATTR, String(ti));
    table.removeAttribute(NEW_TABLE_AFTER_ATTR);
    resetColumnPlan(table);
    // Record the original grid-column count so a later delete of the LAST
    // column can still be detected (the plan alone becomes a prefix and would
    // otherwise look like identity).
    table.setAttribute(COL_COUNT_ATTR, String(gridColumnCount(table)));
    domRows(table).forEach((tr, ri) => {
      tr.setAttribute(ROW_INDEX_ATTR, String(ri));
      Array.from(tr.cells).forEach((td, ci) => td.setAttribute(COL_INDEX_ATTR, String(ci)));
    });
  });
  container.setAttribute(TABLE_COUNT_ATTR, String(tables.length));
}

function readIndex(el: Element, attr: string): number | null {
  const raw = el.getAttribute(attr);
  if (raw === null) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

function isIdentity(plan: Array<number | null>, originalLength: number): boolean {
  if (plan.length !== originalLength) return false;
  return plan.every((v, i) => v === i);
}

/**
 * Compare the current canvas tables against their snapshot and describe the
 * structural changes. Tables the user did not restructure are skipped.
 */
export function collectTableStructurePatches(container: HTMLElement): TableStructurePatch[] {
  if (!container) return [];
  const tables = topLevelTables(container);
  const patches: TableStructurePatch[] = [];
  const seen = new Set<number>();
  const originalCount = readIndex(container, TABLE_COUNT_ATTR) ?? 0;

  for (const table of tables) {
    const tableIndex = readIndex(table, TABLE_INDEX_ATTR);
    if (tableIndex === null) continue;
    seen.add(tableIndex);

    const rows = domRows(table);
    if (rows.length === 0) {
      patches.push({ kind: "delete", tableIndex });
      continue;
    }

    const rowPlan = rows.map((tr) => readIndex(tr, ROW_INDEX_ATTR));
    const originalRowCount =
      rowPlan.reduce<number>((max, v) => (v !== null && v > max ? v : max), -1) + 1;

    // Column edits are replayed from the plan the table carries: merged cells
    // make the current DOM ambiguous about which grid column disappeared.
    const colPlan = readColumnPlan(table);
    // Use the column count recorded at snapshot time — recomputing it from the
    // current plan breaks when the LAST column is deleted (the surviving plan
    // becomes a prefix and looks like identity).
    const originalColCount =
      readIndex(table, COL_COUNT_ATTR) ??
      colPlan.reduce<number>((max, v) => (v !== null && v > max ? v : max), -1) + 1;

    const rowsChanged = !isIdentity(rowPlan, originalRowCount);
    const colsChanged = colPlan.length > 0 && !isIdentity(colPlan, originalColCount);
    if (!rowsChanged && !colsChanged) continue;

    const hasNewColumns = colPlan.some((v) => v === null);
    const newColumnTexts =
      colsChanged && hasNewColumns ? readNewColumnTexts(table, colPlan) : undefined;

    patches.push({
      kind: "update",
      tableIndex,
      rows: rowPlan,
      columns: colsChanged ? colPlan : [],
      ...(newColumnTexts ? { newColumnTexts } : {}),
    });
  }

  // Tables removed from the canvas.
  for (let i = 0; i < originalCount; i++) {
    if (!seen.has(i)) patches.push({ kind: "delete", tableIndex: i });
  }

  // Tables created in the canvas.
  for (const table of tables) {
    if (readIndex(table, TABLE_INDEX_ATTR) !== null) continue;
    // Empty string on the marker means "no anchor". Anchor it to the LAST
    // existing table in the SAME page section, so a table added on page N is
    // inserted in page N's document.xml — not at the end of the whole body
    // (which is the last page).
    const rawAfter = table.getAttribute(NEW_TABLE_AFTER_ATTR);
    let after = rawAfter === "" || rawAfter === null ? null : readIndex(table, NEW_TABLE_AFTER_ATTR);
    if (after === null) after = lastTableIndexInSection(table);
    const rows = domRows(table);
    if (rows.length === 0) continue;
    patches.push({
      kind: "insert",
      afterTableIndex: after,
      cells: rows.map((tr) => Array.from(tr.cells).map((td) => td.textContent ?? "")),
    });
  }

  return patches;
}

/** Original index of the last real (snapshotted) table in the same page section. */
function lastTableIndexInSection(table: HTMLTableElement): number | null {
  const section = table.closest("section.docx") as HTMLElement | null;
  if (!section) return null;
  let max: number | null = null;
  for (const t of topLevelTables(section)) {
    if (t === table) continue;
    const idx = readIndex(t, TABLE_INDEX_ATTR);
    if (idx !== null && (max === null || idx > max)) max = idx;
  }
  return max;
}

/** Read the canvas text of every brand-new column (plan `null`) per row. */
function readNewColumnTexts(
  table: HTMLTableElement,
  colPlan: Array<number | null>
): Record<number, string[]> {
  const out: Record<number, string[]> = {};
  const rows = domRows(table);
  colPlan.forEach((v, gridIndex) => {
    if (v !== null) return;
    const texts: string[] = [];
    for (const tr of rows) {
      let cursor = 0;
      let found = "";
      for (const cell of Array.from(tr.cells)) {
        const span = cell.colSpan || 1;
        if (gridIndex >= cursor && gridIndex < cursor + span) {
          found = cell.textContent ?? "";
          break;
        }
        cursor += span;
      }
      texts.push(found);
    }
    out[gridIndex] = texts;
  });
  return out;
}

/* ------------------------------------------------------------------ */
/*  XML rewriting                                                     */
/* ------------------------------------------------------------------ */

function pickCloneSource(plan: Array<number | null>, at: number): number {
  for (let i = at - 1; i >= 0; i--) {
    if (plan[i] !== null) return plan[i] as number;
  }
  for (let i = at + 1; i < plan.length; i++) {
    if (plan[i] !== null) return plan[i] as number;
  }
  return 0;
}

/** Read `<w:gridSpan w:val="n"/>` of a cell (1 when absent). */
function readGridSpan(cellXml: string): number {
  const m = /<w:gridSpan\s[^>]*w:val="(\d+)"/.exec(cellXml);
  const n = m ? Number(m[1]) : 1;
  return Number.isFinite(n) && n > 0 ? n : 1;
}

/** Write a new grid span, keeping `<w:tcPr>` element order valid. */
function writeGridSpan(cellXml: string, span: number): string {
  const stripped = cellXml.replace(/<w:gridSpan\s[^>]*\/>/g, "");
  if (span <= 1) return stripped;
  const tag = `<w:gridSpan w:val="${span}"/>`;
  if (/<w:tcW\s[^>]*\/>/.test(stripped)) {
    return stripped.replace(/<w:tcW\s[^>]*\/>/, (m) => m + tag);
  }
  if (/<w:tcPr>/.test(stripped)) {
    return stripped.replace("<w:tcPr>", `<w:tcPr>${tag}`);
  }
  return stripped.replace(/(<w:tc(?:\s[^>]*)?>)/, `$1<w:tcPr>${tag}</w:tcPr>`);
}

/**
 * Rebuild the `<w:tc>` list of one row from the grid-column plan. A cell that
 * covers several grid columns is emitted once, with its span adjusted to how
 * many of those columns survived.
 */
function applyColumnPlan(
  rowXml: string,
  plan: Array<number | null>,
  newColumnTexts?: Record<number, string[]>,
  rowIndex = 0
): string {
  const cells = findBlocks(rowXml, "w:tc");
  if (cells.length === 0 || plan.length === 0) return rowXml;

  // Grid column → index of the cell covering it.
  const cellAtGrid = new Map<number, number>();
  let cursor = 0;
  cells.forEach((cell, i) => {
    const span = readGridSpan(cell.xml);
    for (let g = 0; g < span; g++) cellAtGrid.set(cursor + g, i);
    cursor += span;
  });

  const built: string[] = [];
  const used = new Set<number>();
  for (let i = 0; i < plan.length; i++) {
    const source = plan[i];
    if (source === null) {
      const neighbour = cellAtGrid.get(pickCloneSource(plan, i)) ?? cells.length - 1;
      // Keep whatever the user typed into the new column's cell on the canvas
      // (e.g. a `{{field}}` placeholder) instead of writing it empty.
      const text = newColumnTexts?.[i]?.[rowIndex] ?? "";
      const base = clearText(cells[neighbour].xml);
      built.push(writeGridSpan(text ? setCellText(base, text) : base, 1));
      continue;
    }
    const cellIndex = cellAtGrid.get(source);
    if (cellIndex === undefined) continue;
    // Consume every following column that maps to the same merged cell.
    let span = 1;
    while (i + span < plan.length) {
      const next = plan[i + span];
      if (next === null || cellAtGrid.get(next) !== cellIndex) break;
      span++;
    }
    built.push(writeGridSpan(cells[cellIndex].xml, span));
    used.add(cellIndex);
    i += span - 1;
  }

  const dropped = cells.filter((_, i) => !used.has(i)).map((c) => c.xml);
  return replaceSpan(rowXml, cells, reattachLoopTokens(built, dropped).join(""));
}

/** Rebuild `<w:gridCol>` entries so the grid keeps matching the cells. */
function applyGridPlan(tableXml: string, plan: Array<number | null>): string {
  const grids = findBlocks(tableXml, "w:tblGrid");
  if (grids.length === 0 || plan.length === 0) return tableXml;
  const grid = grids[0];
  const cols = findBlocks(grid.xml, "w:gridCol");
  if (cols.length === 0) return tableXml;

  const built = plan
    .map((source, i) => {
      if (source !== null && cols[source]) return cols[source].xml;
      return (cols[pickCloneSource(plan, i)] ?? cols[cols.length - 1]).xml;
    })
    .join("");

  const nextGrid = replaceSpan(grid.xml, cols, built);
  return tableXml.slice(0, grid.start) + nextGrid + tableXml.slice(grid.end);
}

/** Apply the row + column plan of one `<w:tbl>`. */
export function rebuildTableXml(
  tableXml: string,
  rowPlan: Array<number | null>,
  colPlan: Array<number | null>,
  newColumnTexts?: Record<number, string[]>
): string {
  let xml = tableXml;
  if (colPlan.length > 0) xml = applyGridPlan(xml, colPlan);

  const rows = findBlocks(xml, "w:tr");
  if (rows.length === 0) return xml;

  const withColumns =
    colPlan.length > 0
      ? rows.map((r, ri) => applyColumnPlan(r.xml, colPlan, newColumnTexts, ri))
      : rows.map((r) => r.xml);

  const keptSources = new Set(rowPlan.filter((v): v is number => v !== null));
  const droppedRows = withColumns.filter((_, i) => !keptSources.has(i));

  const built = rowPlan.map((source, i) => {
    if (source !== null && withColumns[source] !== undefined) return withColumns[source];
    const clone = withColumns[pickCloneSource(rowPlan, i)] ?? withColumns[withColumns.length - 1];
    return clearText(clone);
  });

  return replaceSpan(xml, rows, reattachLoopTokens(built, droppedRows).join(""));
}

function escapeXmlText(text: string): string {
  return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/** Bordered `<w:tbl>` holding the given cell texts, for a canvas-created table. */
export function buildTableXml(cells: string[][]): string {
  const cols = Math.max(1, ...cells.map((r) => r.length));
  const width = Math.max(1, Math.floor(9000 / cols));
  const borders = ["top", "left", "bottom", "right", "insideH", "insideV"]
    .map((side) => `<w:${side} w:val="single" w:sz="4" w:color="auto"/>`)
    .join("");
  const grid = Array.from({ length: cols }, () => `<w:gridCol w:w="${width}"/>`).join("");

  const body = cells
    .map((row) => {
      const tcs = Array.from({ length: cols }, (_, i) => {
        const text = escapeXmlText(row[i] ?? "");
        return (
          `<w:tc><w:tcPr><w:tcW w:w="${width}" w:type="dxa"/></w:tcPr>` +
          `<w:p><w:r><w:t xml:space="preserve">${text}</w:t></w:r></w:p></w:tc>`
        );
      }).join("");
      return `<w:tr>${tcs}</w:tr>`;
    })
    .join("");

  return (
    `<w:tbl><w:tblPr><w:tblW w:w="0" w:type="auto"/>` +
    `<w:tblBorders>${borders}</w:tblBorders></w:tblPr>` +
    `<w:tblGrid>${grid}</w:tblGrid>${body}</w:tbl>`
  );
}

/** True when the table holds another table — those are left untouched. */
function hasNestedTable(tableXml: string): boolean {
  return findBlocks(tableXml.slice(1, -1), "w:tbl").length > 0;
}

/**
 * Write structural table changes into document.xml. Patches are applied from
 * the last table backwards so earlier offsets stay valid.
 */
export function applyTableStructurePatchesToXml(
  docXml: string,
  patches: TableStructurePatch[]
): ApplyTablePatchesResult {
  if (patches.length === 0) return { xml: docXml, patchedCount: 0, unsupported: [] };

  let xml = docXml;
  let patchedCount = 0;
  const unsupported: TableStructurePatch[] = [];

  const ordered = [...patches].sort((a, b) => {
    const ia = a.kind === "insert" ? (a.afterTableIndex ?? -1) : a.tableIndex;
    const ib = b.kind === "insert" ? (b.afterTableIndex ?? -1) : b.tableIndex;
    return ib - ia;
  });

  for (const patch of ordered) {
    const tables = findBlocks(xml, "w:tbl");

    if (patch.kind === "insert") {
      const anchor =
        patch.afterTableIndex !== null ? tables[patch.afterTableIndex] : undefined;
      const newXml = buildTableXml(patch.cells);
      if (anchor) {
        // Word needs a paragraph between two adjacent tables, else they merge.
        xml = xml.slice(0, anchor.end) + `<w:p/>` + newXml + xml.slice(anchor.end);
      } else {
        const bodyEnd = xml.lastIndexOf("</w:body>");
        if (bodyEnd < 0) {
          unsupported.push(patch);
          continue;
        }
        // In a real Word document the body-level <w:sectPr> must remain the
        // LAST child of <w:body>. Appending a new table after it produces
        // invalid OOXML and Word/docx-preview may silently omit that table.
        const finalSectPr = xml.lastIndexOf("<w:sectPr", bodyEnd);
        const insertAt = finalSectPr >= 0 ? finalSectPr : bodyEnd;
        xml = xml.slice(0, insertAt) + `<w:p/>` + newXml + xml.slice(insertAt);
      }
      patchedCount++;
      continue;
    }

    const target = tables[patch.tableIndex];
    if (!target) {
      unsupported.push(patch);
      continue;
    }

    if (patch.kind === "delete") {
      xml = xml.slice(0, target.start) + xml.slice(target.end);
      patchedCount++;
      continue;
    }

    if (hasNestedTable(target.xml)) {
      unsupported.push(patch);
      continue;
    }

    const next = rebuildTableXml(target.xml, patch.rows, patch.columns, patch.newColumnTexts);
    if (next === target.xml) {
      // The canvas changed but the document did not — say so instead of
      // reporting a save that quietly did nothing.
      unsupported.push(patch);
      continue;
    }
    xml = xml.slice(0, target.start) + next + xml.slice(target.end);
    patchedCount++;
  }

  return { xml, patchedCount, unsupported };
}

/**
 * Read structural table edits from the canvas and write them into the DOCX.
 */
export function applyPreviewTableEditsToDocx(
  fileData: ArrayBuffer | Uint8Array,
  container: HTMLElement
): { fileData: Uint8Array; patchedCount: number; unsupported: TableStructurePatch[] } {
  const patches = collectTableStructurePatches(container);
  if (patches.length === 0) {
    const raw = fileData instanceof Uint8Array ? fileData : new Uint8Array(fileData);
    return { fileData: raw, patchedCount: 0, unsupported: [] };
  }

  const zip = new PizZip(fileData);
  const docXml = repairSplitMarkers(zip.files["word/document.xml"]?.asText() ?? "").xml;
  const { xml, patchedCount, unsupported } = applyTableStructurePatchesToXml(docXml, patches);
  if (patchedCount > 0) zip.file("word/document.xml", xml);

  const next = zip.generate({
    type: "uint8array",
    mimeType:
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  }) as Uint8Array;

  return { fileData: next, patchedCount, unsupported };
}
